/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import { PushNotificationDedupeCache } from "./PushNotificationDedupeCache";
import { PushNotificationRouter } from "./PushNotificationRouter";
import type {
  IPushNotificationModelListener,
  PushNotificationSource,
  TPushNotification
} from "./types";

/**
 * Stage 3a — cross-cutting middleware and model-level fan-out.
 *
 * Every transport funnels its parsed notifications through handleMessage(),
 * so the work that must happen exactly once per notification — logging and
 * duplicate suppression — lives here rather than in each source.
 *
 * Order of operations is deliberate: log, then drop duplicates, then notify
 * the model-level listener tier, then route to the owning feature model.
 */
export class PushNotificationModelImpl {
  private static readonly TAG: string = "PushNotificationModelImpl";

  private static mInstance: PushNotificationModelImpl | null = null;

  /** Bounded record of recently seen correlation ids. */
  private readonly mDedupeCache: PushNotificationDedupeCache =
    new PushNotificationDedupeCache();

  /** Model-level listeners. A Set makes add idempotent. */
  private readonly mListeners: Set<IPushNotificationModelListener> =
    new Set<IPushNotificationModelListener>();

  /**
   * Accept a parsed notification from any transport.
   *
   * @param notification - The parsed notification
   * @param source - The transport that delivered it
   */
  public handleMessage(
    notification: TPushNotification,
    source: PushNotificationSource
  ): void {
    Log.i(
      PushNotificationModelImpl.TAG,
      `handleMessage(): type=${notification.notificationType}, source=${source}, originRequestId=${notification.originRequestId}, rpcId=${notification.rpcId}`
    );

    if (this.isDuplicate(notification)) {
      return;
    }

    this.notifyListeners(notification);

    PushNotificationRouter.route(notification);
  }

  /**
   * Register a model-level listener. Idempotent for the same reference.
   *
   * @param listener - The listener to add
   */
  public addListener(listener: IPushNotificationModelListener): void {
    this.mListeners.add(listener);
    Log.d(
      PushNotificationModelImpl.TAG,
      `addListener(): count=${this.mListeners.size}`
    );
  }

  /**
   * Deregister a model-level listener. A no-op when not registered.
   *
   * @param listener - The listener to remove
   */
  public removeListener(listener: IPushNotificationModelListener): void {
    this.mListeners.delete(listener);
    Log.d(
      PushNotificationModelImpl.TAG,
      `removeListener(): count=${this.mListeners.size}`
    );
  }

  /**
   * Drop all listeners and forget all dedupe state. Idempotent.
   */
  public destroy(): void {
    this.mListeners.clear();
    this.mDedupeCache.clear();
    Log.d(PushNotificationModelImpl.TAG, "destroy(): Cleared listeners");
  }

  /**
   * Test whether this notification repeats one already handled.
   *
   * A notification with no correlation id cannot be deduped — every such
   * notification would otherwise collide on the empty key and only the
   * first would ever be delivered — so it is always accepted.
   *
   * @param notification - The parsed notification
   * @returns True when the notification should be dropped
   */
  private isDuplicate(notification: TPushNotification): boolean {
    if (notification.originRequestId.length === 0) {
      Log.w(
        PushNotificationModelImpl.TAG,
        "isDuplicate(): Missing Origin-RequestId — skipping duplicate check"
      );
      return false;
    }

    const isDuplicate: boolean = this.mDedupeCache.isDuplicate(
      notification.originRequestId,
      notification.receivedTimeMs
    );

    if (isDuplicate) {
      Log.w(
        PushNotificationModelImpl.TAG,
        `isDuplicate(): Dropping duplicate originRequestId=${notification.originRequestId}`
      );
    }

    return isDuplicate;
  }

  /**
   * Notify every model-level listener.
   *
   * Iterates a snapshot so a listener that deregisters itself during the
   * callback cannot corrupt iteration, and isolates each callback so one
   * failing listener cannot starve the rest or block routing.
   *
   * @param notification - The parsed notification
   */
  private notifyListeners(notification: TPushNotification): void {
    const listeners: IPushNotificationModelListener[] = Array.from(
      this.mListeners
    );

    for (const listener of listeners) {
      try {
        listener.onNotificationReceived(notification);
      } catch (error: unknown) {
        Log.e(
          PushNotificationModelImpl.TAG,
          "notifyListeners(): Listener onNotificationReceived()",
          error
        );
      }
    }
  }

  /**
   * Get the shared instance, creating it on first use.
   *
   * @returns The singleton instance
   */
  public static getInstance(): PushNotificationModelImpl {
    if (PushNotificationModelImpl.mInstance === null) {
      PushNotificationModelImpl.mInstance = new PushNotificationModelImpl();
    }
    return PushNotificationModelImpl.mInstance;
  }

  /**
   * Discard the shared instance so the next getInstance() builds a fresh one.
   * Test-only.
   */
  public static __resetForTest(): void {
    PushNotificationModelImpl.mInstance = null;
  }
}
